import React, { memo, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import Pages from "..";
import { TypesAction } from "../../redux/typesAction";
import { Product } from "../../gobalType";
import { usePlantExplorerMobileGate } from "./hooks/usePlantExplorerMobileGate";
import { useAllPlantsSearchList } from "./hooks/useAllPlantsSearchList";
import { buildSortedUniquePlantRows } from "./utils/buildSortedUniquePlantRows";
import SearchExploreHeader from "./components/SearchExploreHeader";
import PlantNameSearchInput from "./components/PlantNameSearchInput";
import PlantNameList from "./components/PlantNameList";
import PlantExplorerSearchSkeleton from "./components/PlantExplorerSearchSkeleton";

const searchScreenSx = {
  bgcolor: "#fff",
  minHeight: "100vh",
  pt: 0,
  pb: 2,
  boxSizing: "border-box",
} as const;

const PlantExplorerSearch: React.FC = () => {
  const isMobile = usePlantExplorerMobileGate();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, plants } = useAllPlantsSearchList(isMobile);
  const [query, setQuery] = useState("");

  const sortedRows = useMemo(
    () => buildSortedUniquePlantRows(plants),
    [plants]
  );

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter((row) => row.name.toLowerCase().includes(q));
  }, [query, sortedRows]);

  const handleBack = useCallback(() => {
    navigate("/plants-explorer");
  }, [navigate]);

  const openProduct = useCallback(
    (item: Product) => {
      dispatch({ type: TypesAction.CATEGORY_ID, payload: 1 });
      dispatch({ type: TypesAction.SHOW_INPUT_SEARCH, payload: true });
      navigate(`/product-details/${item.id}`, {
        state: {
          product_id: item?.product_sizes?.[0]?.product_id,
          size_id: item?.product_sizes?.[0]?.id,
        },
      });
    },
    [dispatch, navigate]
  );

  if (!isMobile) {
    return null;
  }

  if (loading) {
    return (
      <Pages seo={{ title: "Search Plant Explore" }}>
        <PlantExplorerSearchSkeleton />
      </Pages>
    );
  }

  return (
    <Pages seo={{ title: "Search Plant Explore" }}>
      <Box sx={searchScreenSx}>
        <SearchExploreHeader onBack={handleBack} />
        <PlantNameSearchInput value={query} onChange={setQuery} />
        <PlantNameList rows={filteredRows} onSelect={openProduct} />
      </Box>
    </Pages>
  );
};

export default memo(PlantExplorerSearch);
